export const getSessionHidden = (key: string): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { exp?: number } | null;
    if (!parsed?.exp) return false;
    if (Date.now() > parsed.exp) {
      window.sessionStorage.removeItem(key);
      return false;
    }
    return true;
  } catch {
    return false;
  }
};

export const setSessionHidden = (key: string, maxAgeSeconds: number) => {
  if (typeof window === 'undefined') return;
  const exp = Date.now() + maxAgeSeconds * 1000;
  try {
    window.sessionStorage.setItem(key, JSON.stringify({ exp }));
  } catch {
    console.log('Error setting session hidden', key);
  }
};

export const SEARCH_BAR_CONTAINER_SELECTOR = '[data-cy="search-bar-container"]';

export const measureSearchBarBottom = (): number => {
  if (typeof document === 'undefined') return 0;

  const bar = document.querySelector(SEARCH_BAR_CONTAINER_SELECTOR);
  if (!(bar instanceof HTMLElement)) return 0;

  const rect = bar.getBoundingClientRect();
  if (rect.height < 1 || rect.bottom <= 0 || rect.top > 160) return 0;

  return Math.max(0, Math.round(rect.bottom));
};

const SCROLL_LISTEN_OPTS: AddEventListenerOptions = {
  passive: true,
  capture: true
};

export const subscribeViewport = (onUpdate: () => void) => {
  let ticking = false;

  const requestTick = () => {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(() => {
      ticking = false;
      onUpdate();
    });
  };

  window.addEventListener('scroll', requestTick, SCROLL_LISTEN_OPTS);
  window.addEventListener('touchmove', requestTick, SCROLL_LISTEN_OPTS);
  window.addEventListener('resize', requestTick, { passive: true });
  window.visualViewport?.addEventListener('resize', requestTick);
  window.visualViewport?.addEventListener('scroll', requestTick);
  onUpdate();

  return () => {
    window.removeEventListener('scroll', requestTick, { capture: true });
    window.removeEventListener('touchmove', requestTick, { capture: true });
    window.removeEventListener('resize', requestTick);
    window.visualViewport?.removeEventListener('resize', requestTick);
    window.visualViewport?.removeEventListener('scroll', requestTick);
  };
};

export type BannerCreative = {
  href: string;
  imgSrc: string;
};

export const extractBannerCreative = (
  containerId: string
): BannerCreative | null => {
  if (typeof document === 'undefined') return null;

  const container = document.getElementById(containerId);
  if (!container) return null;

  const imgSrc = container.querySelector('img')?.getAttribute('src') || '';
  const href = container.querySelector('a[href]')?.getAttribute('href') || '';
  if (!imgSrc || !href) return null;

  return { imgSrc, href };
};

export const waitForElementById = (
  id: string,
  onFound: (element: HTMLElement) => void,
  maxRaf: number
) => {
  let raf = 0;
  let cancelled = false;

  const tryFind = (attempt: number) => {
    if (cancelled) return;
    const element = document.getElementById(id);
    if (element) {
      onFound(element);
      return;
    }
    if (attempt < maxRaf) {
      raf = window.requestAnimationFrame(() => tryFind(attempt + 1));
    }
  };

  tryFind(0);

  return () => {
    cancelled = true;
    if (raf) window.cancelAnimationFrame(raf);
  };
};
